// frontend/src/components/chat/MessageItem.js
// Mensaje individual - Burbuja de chat

import React, { useState } from 'react';
import './MessageItem.css';

const MessageItem = ({
    message,
    isOwn,
    showAvatar,
    isGroupStart,
    isGroupEnd,
    onReply,
    onDelete,
    onEdit
}) => {
    const [showActions, setShowActions] = useState(false);

    // Formatear hora
    const formatTime = (dateString) => {
        return new Date(dateString).toLocaleTimeString('es-EC', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Formatear tamano de archivo
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Renderizar contenido segun tipo
    const renderContent = () => {
        switch (message.message_type) {
            case 'image':
                return (
                    <div className="urbanq-msg-image">
                        <img
                            src={message.file_url}
                            alt="Imagen"
                            loading="lazy"
                            onClick={() => window.open(message.file_url, '_blank')}
                        />
                    </div>
                );

            case 'file':
                return (
                    <a
                        href={message.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="urbanq-msg-file"
                    >
                        <div className="urbanq-msg-file-icon">
                            📄
                        </div>
                        <div className="urbanq-msg-file-info">
                            <span className="urbanq-msg-file-name">{message.file_name}</span>
                            <span className="urbanq-msg-file-size">{formatFileSize(message.file_size)}</span>
                        </div>
                        <svg viewBox="0 0 24 24" fill="currentColor" className="urbanq-msg-file-download">
                            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
                        </svg>
                    </a>
                );

            case 'audio':
                return (
                    <div className="urbanq-msg-audio">
                        <audio controls src={message.file_url}>
                            Tu navegador no soporta audio
                        </audio>
                    </div>
                );

            case 'system':
                return (
                    <div className="urbanq-msg-system">
                        {message.content}
                    </div>
                );

            default:
                return (
                    <div className="urbanq-msg-text">
                        {message.content}
                    </div>
                );
        }
    };

    // Mensaje del sistema (diferente layout)
    if (message.message_type === 'system') {
        return (
            <div className="urbanq-message-item system">
                <div className="urbanq-msg-system-content">
                    {message.content}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`urbanq-message-item ${isOwn ? 'own' : 'other'} ${isGroupStart ? 'group-start' : ''} ${isGroupEnd ? 'group-end' : ''}`}
            onMouseEnter={() => setShowActions(true)}
            onMouseLeave={() => setShowActions(false)}
        >
            {/* Avatar (solo para mensajes de otros) */}
            {!isOwn && (
                <div className="urbanq-msg-avatar-wrapper">
                    {showAvatar ? (
                        message.sender_avatar ? (
                            <img
                                src={message.sender_avatar}
                                alt=""
                                className="urbanq-msg-avatar"
                            />
                        ) : (
                            <div className="urbanq-msg-avatar-initial">
                                {message.sender_name?.charAt(0).toUpperCase() || '?'}
                            </div>
                        )
                    ) : (
                        <div className="urbanq-msg-avatar-spacer"></div>
                    )}
                </div>
            )}

            <div className="urbanq-msg-content-wrapper">
                {/* Nombre del remitente (en grupos) */}
                {!isOwn && isGroupStart && (
                    <span className="urbanq-msg-sender">{message.sender_name}</span>
                )}

                {/* Respuesta a mensaje */}
                {message.reply_to_message && (
                    <div className="urbanq-msg-reply-preview">
                        <span className="urbanq-msg-reply-name">
                            {message.reply_to_message.sender_name}
                        </span>
                        <span className="urbanq-msg-reply-text">
                            {message.reply_to_message.content}
                        </span>
                    </div>
                )}

                {/* Burbuja del mensaje */}
                <div className={`urbanq-msg-bubble ${message.message_type}`}>
                    {renderContent()}

                    <div className="urbanq-msg-meta">
                        {message.is_edited && (
                            <span className="urbanq-msg-edited">editado</span>
                        )}
                        <span className="urbanq-msg-time">
                            {formatTime(message.created_at)}
                        </span>
                        {isOwn && (
                            <span className={`urbanq-msg-status ${message.read_count > 0 ? 'read' : 'sent'}`}>
                                {message.read_count > 0 ? '✓✓' : '✓'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Acciones */}
                {showActions && (
                    <div className="urbanq-msg-actions">
                        <button
                            className="urbanq-msg-action-btn"
                            onClick={() => onReply?.(message)}
                            title="Responder"
                        >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z"/>
                            </svg>
                        </button>
                        {isOwn && message.message_type === 'text' && (
                            <button
                                className="urbanq-msg-action-btn"
                                onClick={() => onEdit?.(message)}
                                title="Editar"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                                </svg>
                            </button>
                        )}
                        {isOwn && (
                            <button
                                className="urbanq-msg-action-btn danger"
                                onClick={() => onDelete?.(message.id)}
                                title="Eliminar"
                            >
                                <svg viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                                </svg>
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageItem;
